import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { Tag, Tooltip } from "antd";
import Highlighter from "react-highlight-words";
import {
    CheckCircleOutlined,
    StopOutlined,
} from "@ant-design/icons";

import { ICity } from "../../interfaces";
import CitiesService from "../../services/http/services/CitiesService";
import { EmitSuccessAlert } from "../../utils/EmitSuccessAlert";
import { EmitError } from "../../utils/EmitError";
import { formatDateStringToDateAndTime, poeValorComPontosEmMil } from "../../utils/Conversores";
import { removeAcento } from "../../utils/RemoveAcentos";
import useLocalStorage from "../../hooks/useLocalStorage";
import { ThemeContext } from "../../contexts/ThemeContext";
import TableActionButton from "../../components/TableActionButton";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";
import useTableColumnFilter from "../../hooks/useTableColumnFilter";

export interface ITableData extends ICity {
    key: number | undefined;
    city_uf: string;
}

export interface ICityFilters {
    city_uf: string | null;
    status: 'ACTIVE' | 'INACTIVE' | null;
    is_in_test: 'TEST' | 'PROD' | null;
}

export type SortField =
    | 'city_uf'
    | 'app_user_count'
    | 'created_at'
    | 'status'
    | null;

export default function useCities() {
    const { getUser } = useLocalStorage();
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const { getColumnSearchProps, getColumnRadioProps } = useTableColumnFilter();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [cityBeingDeleted, setCityBeingDeleted] = useState<ITableData | null>(null);
    const [rawData, setRawData] = useState<Array<ITableData>>([]);
    const [isLoadingUpload, setIsLoadingUpload] = useState<boolean>(false);
    const [isUpdateCityModalVisible, setIsUpdateCityModalVisible] = useState<boolean>(false);
    const [cityBeingUpdated, setCityBeingUpdated] = useState<ITableData | null>(null);

    const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });

    const [filters, setFilters] = useState<ICityFilters>({
        city_uf: null,
        status: null,
        is_in_test: null,
    });

    const [sorter, setSorter] = useState<{ field: SortField; order: 'asc' | 'desc' | null }>({
        field: 'city_uf',
        order: 'asc',
    });

    const [searchCityUf, setSearchCityUf] = useState('');

    const navigate = useNavigate();

    const canManageCities = useMemo(() => {
        const user = getUser();
        if (user) {
            return user.role.id === 1;
        }
        return false;
    }, [getUser]);

    const handleFilterChange = useCallback((key: keyof ICityFilters, value: any) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({
            city_uf: null,
            status: null,
            is_in_test: null,
        });
        setSearchCityUf('');
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const stats = useMemo(() => {
        const total = rawData.length;
        const ativos = rawData.filter((c) => c.status).length;
        const inativos = rawData.filter((c) => !c.status).length;
        const emTestes = rawData.filter((c) => c.is_in_test).length;

        return { total, ativos, inativos, emTestes };
    }, [rawData]);

    const filteredData = useMemo(() => {
        let result = rawData;

        if (filters.city_uf) {
            const query = removeAcento(filters.city_uf.trim().toLowerCase());
            result = result.filter((item) =>
                item.city_uf ? removeAcento(item.city_uf.toLowerCase()).includes(query) : false
            );
        }

        if (filters.status) {
            if (filters.status === 'ACTIVE') {
                result = result.filter((item) => !!item.status);
            } else if (filters.status === 'INACTIVE') {
                result = result.filter((item) => !item.status);
            }
        }

        if (filters.is_in_test) {
            if (filters.is_in_test === 'TEST') {
                result = result.filter((item) => !!item.is_in_test);
            } else if (filters.is_in_test === 'PROD') {
                result = result.filter((item) => !item.is_in_test);
            }
        }

        if (sorter.field && sorter.order) {
            result = [...result].sort((a, b) => {
                if (sorter.field === 'city_uf') {
                    const comp = (a.city_uf || '').localeCompare(b.city_uf || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'app_user_count') {
                    const valA = (a.app_user_count || 0) + (a.panel_user_count || 0);
                    const valB = (b.app_user_count || 0) + (b.panel_user_count || 0);
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'created_at') {
                    const valA = new Date(a.created_at || 0).getTime();
                    const valB = new Date(b.created_at || 0).getTime();
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'status') {
                    const valA = a.status ? 1 : 0;
                    const valB = b.status ? 1 : 0;
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                return 0;
            });
        }

        return result;
    }, [rawData, filters, sorter]);

    const activeFilterItems = useMemo<IActiveFilterItem[]>(() => {
        const items: IActiveFilterItem[] = [];

        if (filters.city_uf) {
            items.push({
                key: 'city_uf',
                label: 'Município',
                value: filters.city_uf,
                onRemove: () => {
                    setFilters((prev) => ({ ...prev, city_uf: null }));
                    setSearchCityUf('');
                },
            });
        }

        if (filters.status) {
            items.push({
                key: 'status',
                label: 'Status',
                value: filters.status === 'ACTIVE' ? 'Ativo' : 'Inativo',
                onRemove: () => setFilters((prev) => ({ ...prev, status: null })),
            });
        }

        if (filters.is_in_test) {
            items.push({
                key: 'is_in_test',
                label: 'Ambiente',
                value: filters.is_in_test === 'TEST' ? 'Em testes' : 'Produção',
                onRemove: () => setFilters((prev) => ({ ...prev, is_in_test: null })),
            });
        }

        return items;
    }, [filters]);

    const totalCitiesCount = filteredData.length;

    const tableData = useMemo(() => {
        const startIndex = (pagination.current - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        return filteredData.slice(startIndex, endIndex);
    }, [filteredData, pagination]);

    const getSortOrder = useCallback(
        (field: SortField) => {
            if (sorter.field === field) {
                return sorter.order === 'asc'
                    ? ('ascend' as const)
                    : sorter.order === 'desc'
                    ? ('descend' as const)
                    : null;
            }
            return null;
        },
        [sorter]
    );

    const handleTableChange = (
        _pagination: any,
        _filters: any,
        sorterResult: any
    ) => {
        if (sorterResult && sorterResult.field) {
            const field = Array.isArray(sorterResult.field)
                ? (sorterResult.field[0] as SortField)
                : (sorterResult.field as SortField);
            const order =
                sorterResult.order === 'ascend'
                    ? 'asc'
                    : sorterResult.order === 'descend'
                    ? 'desc'
                    : null;
            setSorter({ field, order });
        } else {
            setSorter({ field: 'city_uf', order: 'asc' });
        }
    };

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setPagination({ current: page, pageSize });
    }, []);

    const handleClickEditCity = useCallback((data: ICity) => {
        navigate(`/municipios/editar/${data.id}`);
    }, [navigate]);

    const handleDeleteCity = useCallback((data: ICity) => {
        setIsDeleteModalVisible(true);
        setCityBeingDeleted(rawData.find((element) => element.id === data.id) || null);
    }, [rawData]);

    const handleUpdateCity = useCallback((data: ICity) => {
        setIsUpdateCityModalVisible(true);
        setCityBeingUpdated(rawData.find((element) => element.id === data.id) || null);
    }, [rawData]);

    const setTableDataFromRequest = useCallback((data: Array<ICity>) => {
        const newData = data.map((city) => ({
            ...city,
            key: city.id,
            city_uf: `${city.city} - ${city.uf}`,
        }));
        setRawData(newData);
    }, []);

    const handleConfirmUpdateCity = useCallback(async () => {
        try {
            setIsLoadingUpload(true);

            const controller = new AbortController();
            const formData = new FormData();
            const status = String(!cityBeingUpdated?.status);
            formData.append('status', status);

            await CitiesService.updateCity(cityBeingUpdated?.id!, formData);
            EmitSuccessAlert('Status do município atualizado com sucesso!');
            const cities = await CitiesService.listCities(controller.signal);

            setTableDataFromRequest(cities);
            setIsUpdateCityModalVisible(false);
            setCityBeingUpdated(null);
            setIsLoadingUpload(false);
        } catch (err) {
            EmitError(err, true, 'cityStatusUpdate_oasdajsdosp');
            setIsLoadingUpload(false);
        }
    }, [cityBeingUpdated, setTableDataFromRequest]);

    const handleCancelUpdateCity = useCallback(() => {
        setIsUpdateCityModalVisible(false);
        setCityBeingUpdated(null);
    }, []);



    const columns = [
        {
            title: 'Município / UF',
            dataIndex: 'city_uf',
            key: 'city_uf',
            sorter: true,
            sortOrder: getSortOrder('city_uf'),
            showSorterTooltip: false,
            width: 320,
            ...getColumnSearchProps({
                title: 'Buscar Município',
                placeholder: 'Digite o município ou UF...',
                value: searchCityUf,
                onChange: setSearchCityUf,
                onSearch: (val) => handleFilterChange('city_uf', val),
                isActive: !!filters.city_uf,
            }),
            render: (text: string, record: ITableData) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span
                            style={{
                                fontWeight: 600,
                                color: isDark ? '#f0f0f0' : '#1e293b',
                                fontSize: 13,
                                lineHeight: 1.3,
                            }}
                        >
                            {filters.city_uf ? (
                                <Highlighter
                                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                    searchWords={[filters.city_uf]}
                                    autoEscape
                                    textToHighlight={record.city || text || '--'}
                                />
                            ) : (
                                record.city || text || '--'
                            )}
                        </span>
                        <Tag
                            color="blue"
                            style={{ margin: 0 }}
                        >
                            {record.uf}
                        </Tag>
                        {record.is_in_test && (
                            <Tag
                                color="warning"
                                style={{ margin: 0 }}
                            >
                                Em testes
                            </Tag>
                        )}
                    </div>
                    {record.name ? (
                        <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 12 }}>
                            {record.name}
                        </span>
                    ) : null}
                </div>
            ),
        },
        {
            title: 'Usuários (Painel / App)',
            dataIndex: 'app_user_count',
            key: 'app_user_count',
            width: 220,
            sorter: true,
            sortOrder: getSortOrder('app_user_count'),
            showSorterTooltip: false,
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <Tooltip title="Usuários com acesso ao Painel Administrativo">
                        <Tag color="default" style={{ margin: 0 }}>
                            Painel: {poeValorComPontosEmMil(record.panel_user_count || 0)}
                        </Tag>
                    </Tooltip>
                    <Tooltip title="Usuários cidadãos cadastrados no Aplicativo">
                        <Tag color="processing" style={{ margin: 0 }}>
                            App: {poeValorComPontosEmMil(record.app_user_count || 0)}
                        </Tag>
                    </Tooltip>
                </div>
            ),
        },
        {
            title: 'Data de Cadastro',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            sorter: true,
            sortOrder: getSortOrder('created_at'),
            showSorterTooltip: false,
            render: (criado: any) => (
                <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {criado ? formatDateStringToDateAndTime(criado) : '--'}
                </span>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center' as const,
            width: 120,
            sorter: true,
            sortOrder: getSortOrder('status'),
            showSorterTooltip: false,
            ...getColumnRadioProps({
                title: 'Filtrar por Status',
                value: filters.status || 'ALL',
                onChange: (val) => handleFilterChange('status', val === 'ALL' ? null : val),
                onClear: () => handleFilterChange('status', null),
                isActive: !!filters.status,
                options: [
                    { label: 'Todos', value: 'ALL' },
                    { label: 'Ativos', value: 'ACTIVE' },
                    { label: 'Inativos', value: 'INACTIVE' },
                ],
            }),
            render: (status: boolean) => (
                <Tag
                    color={status ? 'green' : 'red'}
                    style={{ margin: 0 }}
                >
                    {status ? 'Ativo' : 'Inativo'}
                </Tag>
            ),
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: 120,
            fixed: 'right' as const,
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <TableActionButton
                        action="edit"
                        tooltip="Editar município"
                        onClick={() => handleClickEditCity(record)}
                    />
                    <TableActionButton
                        action="custom"
                        tooltip={record.status ? "Inativar município" : "Ativar município"}
                        icon={
                            record.status ? (
                                <StopOutlined style={{ fontSize: 14, color: '#fa8c16' }} />
                            ) : (
                                <CheckCircleOutlined style={{ fontSize: 14, color: '#52c41a' }} />
                            )
                        }
                        color={record.status ? '#fa8c16' : '#52c41a'}
                        bgColor={
                            record.status
                                ? isDark
                                    ? 'rgba(250, 140, 22, 0.12)'
                                    : '#fff7e6'
                                : isDark
                                ? 'rgba(82, 196, 26, 0.12)'
                                : '#f6ffed'
                        }
                        onClick={() => handleUpdateCity(record)}
                    />
                    {canManageCities && (
                        <TableActionButton
                            action="delete"
                            tooltip="Excluir município"
                            onClick={() => handleDeleteCity(record)}
                        />
                    )}
                </div>
            ),
        },
    ];

    useEffect(() => {
        const controller = new AbortController();

        async function loadCities() {
            try {
                setIsLoading(true);
                const cities = await CitiesService.listCities(controller.signal);

                setTableDataFromRequest(cities);
                setIsLoading(false);

                const user = getUser();
                if (user) {
                    if (user.role.id !== 1 && user.cities.length === 1) {
                        navigate('/municipios/editar/' + user.cities[0].id);
                        return;
                    }
                }
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'cities_asdhashuidd');
                setIsLoading(false);
            }
        }

        loadCities();

        return () => {
            controller.abort();
        };
    }, [setTableDataFromRequest, navigate, getUser]);

    const handleConfirmDeleteCity = useCallback(async () => {
        try {
            setIsLoadingDelete(true);
            await CitiesService.deleteCity(cityBeingDeleted?.id!);
            EmitSuccessAlert("Município deletado com sucesso!");
            setRawData((prevState) => prevState.filter((element) => element.id !== cityBeingDeleted?.id));
            setIsDeleteModalVisible(false);
            setCityBeingDeleted(null);
            setIsLoadingDelete(false);
        } catch (err) {
            EmitError(err);
            setIsLoadingDelete(false);
        }
    }, [cityBeingDeleted]);

    const handleCancelDeleteCity = useCallback(() => {
        setIsDeleteModalVisible(false);
        setCityBeingDeleted(null);
    }, []);

    const handleClickCreateNewCity = useCallback(() => {
        navigate('/municipios/criar');
    }, [navigate]);

    return {
        isLoading,
        isDeleteModalVisible,
        isLoadingDelete,
        handleClickCreateNewCity,
        handleConfirmDeleteCity,
        handleCancelDeleteCity,
        columns,
        tableData,
        totalCitiesCount,
        stats,
        pagination,
        activeFilterItems,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        cityBeingDeleted,
        canManageCities,
        handleCancelUpdateCity,
        handleConfirmUpdateCity,
        cityBeingUpdated,
        isLoadingUpload,
        isUpdateCityModalVisible,
        handleClickEditCity,
        navigate,
    };
}
